import type { CatalogBlock, WebInvoiceLine } from "../../lib/invoices";
import InvoiceTitelFeld from "./InvoiceTitelFeld";

/**
 * Der Kopf eines Blocks — flach gelesen, auf Klick ein Formular.
 *
 * Die Zwischensumme steht nicht mehr hier: im Zieldesign (Claude Design,
 * 01.09.2026) folgt sie den Positionen als eigene Zeile, wie im PDF auch —
 * `InvoicePositionEditor` rendert sie nach `invoice-block-lines`. Der Kopf
 * selbst ist standardmäßig nur Text (Überschrift, Zusatztext, Anzahl); erst
 * ein Klick öffnet die Eingabefelder. Genau dieselbe Meile wie bei einer
 * einzelnen Position (`InvoiceLineRowFlat`/`InvoiceLineRow`) — ein Beleg mit
 * mehreren Blöcken soll sich lesen wie eine Tabelle, nicht wie ein Stapel
 * Formulare.
 */

export type BlockHeadProps = {
  block: WebInvoiceLine;
  anzahl: number;
  katalogLaeuft: boolean;
  /** Für die Zwischenablage ausgewählt — Block und Positionen wandern zusammen. */
  gewaehlt: boolean;
  /** Eingeklappt: nur der Kopf steht, Zusatztext und Positionen sind ausgeblendet. */
  eingeklappt: boolean;
  onToggleEingeklappt: () => void;
  /** Formularfelder zeigen statt Fließtext. */
  bearbeitet: boolean;
  onBearbeiten: () => void;
  onToggleWahl: () => void;
  onChange: (patch: Partial<WebInvoiceLine>) => void;
  onDuplicate: () => void;
  onSaveToCatalog: () => void;
  /** Ein Baustein aus der Bibliothek wurde in der Überschrift gewählt. */
  onKatalogBlock: (block: CatalogBlock) => void;
  onDissolve: () => void;
  onRemove: () => void;
  ziehen: {
    onDragStart: (event: React.DragEvent) => void;
    onDragEnd: () => void;
    ziehtGerade: boolean;
  };
};

export default function InvoiceBlockHead({
  block, anzahl, katalogLaeuft, gewaehlt, eingeklappt, bearbeitet,
  onToggleEingeklappt, onBearbeiten, onToggleWahl, onChange,
  onDuplicate, onSaveToCatalog, onKatalogBlock, onDissolve, onRemove, ziehen,
}: BlockHeadProps) {
  return (
    <div
      className={"invoice-block-head"
        + (gewaehlt ? " gewaehlt" : "")
        + (eingeklappt ? " is-eingeklappt" : "")
        + (bearbeitet ? " is-bearbeitet" : " ist-flach")
        + (ziehen.ziehtGerade ? " is-zieht" : "")}
      draggable={!bearbeitet}
      onDragStart={ziehen.onDragStart}
      onDragEnd={ziehen.onDragEnd}
      onClick={(event) => { if (!bearbeitet && event.target === event.currentTarget) onToggleEingeklappt(); }}
    >
      <div className="invoice-line-order">
        <label className="invoice-wahl" onClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            checked={gewaehlt}
            onChange={onToggleWahl}
            aria-label={`Block „${block.title || "ohne Überschrift"}" mit allen Positionen auswählen`}
          />
        </label>
        {bearbeitet
          ? <strong>Block</strong>
          : <span className="invoice-block-griff" aria-hidden="true">⠿</span>}
      </div>

      {bearbeitet ? (
        <>
          {/* Dasselbe Feld wie im Positionstitel, nur mit der anderen
              Betriebsart: hier schlägt die Bibliothek Bausteine vor, dort
              Positionen. Wer keinen Vorschlag wählt, schreibt freie
              Überschrift — die Suche drängt sich nicht auf. */}
          <label className="invoice-editor-field invoice-block-title">
            <span>Überschrift</span>
            <InvoiceTitelFeld
              art="block"
              wert={block.title}
              onWert={(titel) => onChange({ title: titel })}
              onBlock={onKatalogBlock}
              autoFocus
            />
          </label>
          {!eingeklappt && (
            <label className="invoice-editor-field invoice-block-description">
              <span>Zusatztext (optional)</span>
              <textarea
                rows={2}
                value={block.description}
                onChange={(event) => onChange({ description: event.target.value })}
                placeholder="Erläuterung, die unter der Überschrift steht"
              />
            </label>
          )}
        </>
      ) : (
        /* Kein eigener Klapp-Knopf (Benjamin, 02.09.2026): der Text öffnet das
           Formular, die leere Fläche der Zeile klappt den Block auf und zu.
           Die Anzahl rechts ist der Tastaturweg zum Klappen — ein Knopf, der
           nicht wie einer aussieht, mit dem Pfeil als Zustandsanzeige. */
        <div
          className="invoice-block-kopf-flach"
          onClick={(event) => { if (event.target === event.currentTarget) onToggleEingeklappt(); }}
        >
          <button type="button" className="invoice-block-titel-flach" onClick={onBearbeiten}>
            <strong>{block.title.trim() || "Block ohne Überschrift"}</strong>
            {block.description.trim() && <small>{block.description.trim()}</small>}
          </button>
          <button
            type="button"
            className="invoice-block-anzahl"
            onClick={onToggleEingeklappt}
            aria-expanded={!eingeklappt}
            title={eingeklappt ? "Block aufklappen" : "Block einklappen — nur der Kopf bleibt sichtbar"}
          >
            {anzahl === 1 ? "1 Position" : `${anzahl} Positionen`}
            <span className="invoice-block-chevron" data-eingeklappt={eingeklappt ? "true" : "false"} aria-hidden="true">▾</span>
          </button>
        </div>
      )}

      <div className="invoice-line-tools">
        <button
          type="button"
          onClick={onSaveToCatalog}
          disabled={katalogLaeuft || anzahl === 0}
          title={anzahl === 0
            ? "Ein Block ohne Positionen lässt sich nicht speichern"
            : "Block als Baustein in die Bibliothek übernehmen"}
        >
          ▤
        </button>
        <button type="button" onClick={onDuplicate} title="Block mit allen Positionen duplizieren">⧉</button>
        <button
          type="button"
          onClick={onDissolve}
          title="Block auflösen — die Überschrift geht, die Positionen bleiben"
        >
          ⇱
        </button>
        <button
          className="invoice-line-remove"
          type="button"
          onClick={onRemove}
          aria-label="Block mit allen Positionen entfernen"
          title="Block mit allen Positionen entfernen"
        >
          ×
        </button>
      </div>
    </div>
  );
}
